import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import NativeOrWeb from '../../../../utils/native';
import { toast } from 'react-toastify';
import IconToast from '../../../NewToast';
import { injectIntl } from 'react-intl';
import { useTypewriter } from '../../../../utils/useTypewriter';

/**
 * The box every AI drawer hands its answer back in.
 *
 * SIZED BY ITS CONTENT, between a floor and a cap. It used to be
 * `height: calc(<window.innerHeight> - <a constant per drawer>)`, a number that
 * knew nothing about what was actually above it and that the drawer body — which
 * scrolls already — never needed. On a phone it was wrong in both directions at
 * once:
 *
 *  - Too tall. 短片腳本生成器 stacks two of these and renders both before
 *    anything is generated, so opening that drawer at 390x745 put 581px of EMPTY
 *    box between the adviser and the end of the form; at 390x900, 736px. The
 *    taller the phone, the more dead space — the constant was subtracted from
 *    the viewport, so the box grew with the screen instead of with the answer.
 *  - Too short. 改寫訊息 subtracted 550, which at 360x560 left `calc(560px -
 *    550px)` — a TEN pixel window onto a 1,953px answer. The text was there and
 *    there was no way to reach it.
 *
 * The floor is what the empty state needs to read as a box rather than a line;
 * the cap is what stops a long answer from pushing the form that produced it off
 * the screen. Between them the box simply fits its text and never scrolls at all,
 * which is the common case for a two-line 個性化訊息.
 */
const MIN_HEIGHT = 150;
/**
 * Viewport-relative so it shrinks on a small phone, with an absolute ceiling so
 * it stops growing on a tablet or on the desktop drawer, where 60% of a tall
 * window is more answer than anyone reads without scrolling anyway.
 */
const MAX_HEIGHT = 'min(60vh, 480px)';

/** Padding on the three sides with nothing floating over them. */
const PADDING = 12;
/** The fourth: the copy button's corner, kept clear of the text underneath. */
const COPY_GUTTER = 44;

const ContentBox = props => {
  const { intl, text, disableCopy, disableTypewriter, minHeight = MIN_HEIGHT, maxHeight = MAX_HEIGHT } = props;

  // The answer arrives in one frame; this hands it over a few seconds instead.
  // Only the DISPLAY is progressive — `text` stays whole everywhere it matters,
  // so copying mid-reveal still copies the entire answer (see `copy` below) and
  // the placeholder still keys off the real value rather than what has been
  // typed out so far.
  const { shown, isTyping, reveal } = useTypewriter(text, { enabled: !disableTypewriter });

  const copy = () => {
    NativeOrWeb.copyToClipboard(text)
      .then(() => {
        toast.info(IconToast('info', intl.formatMessage({ id: 'Copied!' })), { className: 'new-toast' });
      })
      .catch(console.log);
  };

  return (
    <div
      data-content-box=""
      style={{
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: 4,
        whiteSpace: 'pre-wrap',
        position: 'relative',
        // Border-box on both bounds, so 150 is the box an adviser sees and not
        // 150 plus whatever the border and padding come to.
        boxSizing: 'border-box',
        minHeight: minHeight,
        maxHeight: maxHeight,
        // The column is what lets the scroller below take the leftover height:
        // it fills the box when the answer is long, and lets the box shrink to
        // the text when it is short.
        display: 'flex',
        flexDirection: 'column',
        // Keeps the answer's first and last lines inside the rounded corners.
        overflow: 'hidden'
      }}
    >
      {!disableCopy && (
        <IconButton
          edge="end"
          color="primary"
          onClick={copy}
          disabled={!text}
          size="small"
          sx={{
            // The corner, not the middle. At `top: 50%` this sat squarely on a
            // line of the answer — and the text's own `zIndex: 2` could never
            // lift it clear, because z-index is ignored on a static box. The
            // scroller's right padding below is what keeps a line from running
            // under it now.
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important',
            '&:hover': {
              backgroundColor: 'rgba(57, 102, 248, 0.08) !important',
              transform: 'scale(1.15) !important'
            },
            '&:active': {
              transform: 'scale(0.95) !important'
            }
          }}
        >
          <FileCopyOutlinedIcon style={{ fontSize: 20 }} />
        </IconButton>
      )}

      {/* https://github.com/stipsan/react-spring-bottom-sheet/issues/68#issuecomment-757073061 */}
      <div
        data-content-box-scroll=""
        data-body-scroll-lock-ignore="true"
        style={{
          flex: 1,
          // Without this a flex item refuses to shrink below its content, and
          // the cap above would be pushed open by a long answer instead of
          // turning it into a scroll.
          minHeight: 0,
          // Deliberately WITHOUT `-webkit-overflow-scrolling: touch`, which the
          // drawer body around it does carry. In WKWebView that promotes a
          // scroller to its own composited layer, and a composited scroller is
          // not clipped by an ancestor's border-radius — which this box now has
          // together with `overflow: hidden`, so the answer's corners would
          // square off against the rounded ones. It buys nothing to weigh
          // against that: momentum is the default for `overflow: auto` from
          // iOS 13 on, and this app's iOS floor is 15.6. The box scrolled with
          // plain `overflow: auto` before this file was restructured.
          overflow: 'auto',
          padding: PADDING,
          paddingRight: disableCopy ? PADDING : COPY_GUTTER,
          // The 24px phantom scroll: this used to be `height: 100%` with the
          // same padding and no border-box, and this app ships no global reset
          // (there is no CssBaseline), so its border-box height came to the
          // scrollport plus exactly its own padding. Every box scrolled by 24px
          // — the empty ones included, which is what an adviser noticed first.
          boxSizing: 'border-box',
          // Anywhere in the answer is the skip target. A dedicated 「跳過」
          // button would be a second control in a box that already has the
          // copy one, for an affordance most advisers use once and then
          // stop thinking about.
          cursor: isTyping ? 'pointer' : undefined
        }}
        onClick={isTyping ? reveal : undefined}
      >
        {!text && (
          // Inside the scroller rather than absolutely positioned over the box,
          // so it picks up the same padding the answer will land in.
          <Typography style={{ color: 'rgba(0, 0, 0, 0.3654)' }}>
            {disableCopy
              ? intl.formatMessage({ id: 'chat-gpt-content-placeholder-2' })
              : intl.formatMessage({ id: 'chat-gpt-content-placeholder' })}
          </Typography>
        )}

        <span
          // Mid-reveal the visible text is a partial sentence, which is worse
          // than useless read aloud. Assistive tech gets the finished answer
          // from the sibling below instead, at once, and is spared the
          // hundreds of intermediate updates the reveal would otherwise
          // announce.
          aria-hidden={isTyping || undefined}
        >
          {shown}
          {isTyping && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-block',
                width: '2px',
                height: '1em',
                marginLeft: '2px',
                verticalAlign: 'text-bottom',
                backgroundColor: 'rgba(57, 102, 248, 0.85)',
                animation: 'contentBoxCaret 1s steps(1) infinite',
                '@keyframes contentBoxCaret': {
                  '0%, 50%': { opacity: 1 },
                  '50.01%, 100%': { opacity: 0 }
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none'
                }
              }}
            />
          )}
        </span>
      </div>

      {isTyping && (
        <Box
          component="div"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        >
          {text}
        </Box>
      )}
    </div>
  );
};

export default injectIntl(ContentBox);
